import React from 'react'
import styled from 'styled-components'
import DOMPurify from 'isomorphic-dompurify'

import {
  TabletHidden,
  TabletOnly
} from '@jarvis.oddle/core/components/Responsive'
import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import Div from '@jarvis.oddle/core/components/Div'
import Button from '@jarvis.oddle/core/components/Button'
import ExternalContent from '@jarvis.oddle/core/components/ExternalContent'
import { H3, H4 } from '@jarvis.oddle/core/components/Typo'
import {
  margin,
  marginSizes,
  padding,
  paddingSizes,
  buttonRadiusValues,
  typo
} from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { ImageContainer, ProductImage } from './Elements'
import useCustomisation from 'src/hooks/useCustomisation'
import { useStoreItemAvailability } from 'src/containers/StoreItemAvailability'
import { useAppContext } from 'src/containers/AppContext'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import {
  getAllProductNutrigradeTags,
  getDisplayPriceFromItem,
  getProductButtonProps
} from 'src/utils/product'
import {
  checkExistNutriGradeTag,
  NutriGradeIcon
} from 'src/components/icons/NutriGrade'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'

const StyledCard = styled(Card)`
  height: 100%;
  padding: ${padding[paddingSizes.small]};
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.small]};
  `}
`

const StyledImageContainer = styled(ImageContainer)`
  width: 95px;
  ${breakpoint('tablet')`
    width: 130px;
  `}

  ${breakpoint('desktop')`
    width: 200px;
  `}
`

const StyledProductImage = styled(ProductImage)`
  margin-right: ${margin[marginSizes.extraSmall]};
  ${breakpoint('tablet')`
    margin-right: ${margin[marginSizes.medium]};
  `}

  ${breakpoint('desktop')`
    max-width: 200px;
    max-height: 200px;
  `}
`

const PriceContainer = styled(Flex)`
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: ${margin[marginSizes.small]};
  ${breakpoint('tablet')`
    flex-direction: column;
    margin-top: ${margin[marginSizes.none]};
  `}
`

const Title = styled(H3)`
  margin: 0 0 ${margin[marginSizes.small]} 0;
  display: inline;
`

const PriceMobileContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column'
}))`
  margin-right: ${margin[marginSizes.extraSmall]};

  /* minus the width of the button */
  flex: 0 0 calc(100% - 110px);
`

const Price = styled(H4)`
  ${breakpoint('tablet')`
    margin: ${margin[marginSizes.small]} 0;
  `}
`

const DetailsContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column',
  justifyContent: 'space-between'
}))`
  /* stylelint-disable length-zero-no-unit */
  flex: 1 1 0;
  /* stylelint-enable length-zero-no-unit */

  width: 50%;
  padding: ${paddingSizes.small} ${paddingSizes.small} ${paddingSizes.small}
    ${paddingSizes.none};
  ${breakpoint('tablet')`
    padding: ${paddingSizes.small};
  `}
`

const StyledProductDescription = styled(H4)`
  * {
    font-family: ${(props) =>
      props.fontFamily || props.theme.typographyHeadings};
    margin: 0 !important;
  }
`

const StyledButton = styled(Button)`
  padding: ${padding[paddingSizes.small]};
  min-width: 110px;
`

const OverlayNutriGradeImage = styled.div`
  position: absolute;
  bottom: 0;
  background: #f2f2f26b;
  width: 100%;
  transition: 0.5s ease;
  color: white;
  text-align: left;
  padding: 5px;
  padding-left: ${padding[paddingSizes.medium]};
  display: block;
  ${breakpoint('mobile')`
    padding-left: ${padding[paddingSizes.extraSmall]};
    display: flex;
    justify-content: center;
    max-height: 49%;
    svg {
      max-height: 40px;
      max-width: 80px;
    }
  `}
  ${breakpoint('tablet')`
    padding-left: ${padding[paddingSizes.extraSmall]};
    display: flex;
    justify-content: center;
    max-height: 30%;
    svg {
      max-height: unset;
      max-width: unset;
    }
  `}
  ${breakpoint('desktop')`
    display: block;
  `}
`

const ProductTagContainer = styled.div`
  padding: 10px 0;
  display: flex;
  flex-flow: wrap;
  grid-gap: 5px;
`

const ProductTag = styled.div`
  display: inline-block;
  background: white;
  padding: 4px 5px;
  border: 1px solid #bbb;
  color: black;
  font-family: 'Roboto', sans-serif;
  border-radius: ${(props) => buttonRadiusValues[props.theme.buttonShape]};
  font-size: ${typo.smallText.desktopFontSize};
`

const ProductListCard = React.memo(
  ({
    product,
    category,
    showImage,
    onButtonClick,
    showDefaultImage,
    productImageAspect = 'cover'
  }: {
    product: Menu.MenuItem
    category: Menu.MenuData
    showImage: boolean
    onButtonClick: (params?) => void
    showDefaultImage: boolean
    productImageAspect: string
  }): JSX.Element => {
    const { t } = React.useContext(TranslationContext)
    const { formatMoney, getProductImagePath } = useAppContext()
    const { previewMode } = useCustomisation()
    const existNutriGradeTag = checkExistNutriGradeTag(product.tags)
    const productNutrigradeTags = getAllProductNutrigradeTags(product.tags)
    const metaState = useAppSelector(selectDetailMenuSite)
    const { enableTimeLimitUnavailability } = metaState?.features || {}

    const {
      text: productButtonText,
      disabled,
      loading
    } = getProductButtonProps(
      t,
      product,
      category,
      useStoreItemAvailability({ product, category }),
      enableTimeLimitUnavailability
    )
    const productImage = getProductImagePath(
      product.imageFullUrls?.[0] || product.imageUrls?.[0],
      showDefaultImage,
      {
        shouldCache: true,
        containerWidth: 250,
        containerHeight: 250,
        objectFit: productImageAspect,
        useUcareCdnScaleCrop: true
      }
    )
    const { preposition, displayPrice } = getDisplayPriceFromItem(
      t,
      product,
      formatMoney
    )

    return (
      <StyledCard>
        <Flex flexDirection='row'>
          {showImage && productImage && (
            <Flex flexDirection='column' marginRight={marginSizes.small}>
              <StyledImageContainer>
                <StyledProductImage
                  alt={product.name}
                  data-src={productImage}
                  objectFit={productImageAspect}
                  lazy
                />
                {existNutriGradeTag &&
                  !productImage.includes('default-product.png') && (
                    <OverlayNutriGradeImage>
                      <NutriGradeIcon
                        tags={product.tags}
                        height={50}
                        width={109}
                      />
                    </OverlayNutriGradeImage>
                  )}
              </StyledImageContainer>
            </Flex>
          )}
          <DetailsContainer>
            <Div>
              <Title bold>{product.name}</Title>
              {(!showImage ||
                !productImage ||
                productImage.includes('default-product.png')) &&
                (product?.tags?.length || 0) > 0 && (
                  <>
                    <br />
                    <NutriGradeIcon
                      tags={product.tags}
                      height={40}
                      width={79}
                    />
                  </>
                )}
              {(productNutrigradeTags?.length || 0) > 0 && (
                <ProductTagContainer>
                  {productNutrigradeTags?.map((tag) => (
                    <ProductTag key={tag}>{tag}</ProductTag>
                  ))}
                </ProductTagContainer>
              )}
              {product.description && (
                <ExternalContent previewMode={previewMode}>
                  <StyledProductDescription
                    line={2}
                    normalised
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(product.description)
                    }}
                  />
                </ExternalContent>
              )}
            </Div>
            <PriceContainer>
              <TabletOnly>
                <Price>{`${preposition} ${displayPrice}`}</Price>
              </TabletOnly>
              <TabletHidden>
                <PriceMobileContainer flexDirection='column'>
                  {preposition && <Price normalised>{`${preposition} `}</Price>}
                  <Price normalised>{displayPrice}</Price>
                </PriceMobileContainer>
              </TabletHidden>
              <StyledButton
                disabled={disabled || loading}
                onClick={onButtonClick}
                loading={loading}
              >
                {productButtonText}
              </StyledButton>
            </PriceContainer>
          </DetailsContainer>
        </Flex>
      </StyledCard>
    )
  }
)

export default ProductListCard
